<template>
  <MainLayout
    no-toolbar
    @scroll="handleLayoutScroll"
  >
    <template #content>
      <div
        v-if="showLoading"
        class="w-full flex justify-center py-24"
      >
        <BaseLoadingSpinner />
      </div>
      <template v-else>
        <div class="max-w-5xl mx-auto w-full">
          <!-- Phone: read-only page, hands the real setup off to a computer -->
          <SetupPhoneHandoff
            v-if="isPhone && !getSetupComplete"
            class="mb-4"
          />

          <SetupHeader
            :copy="headerCopy"
            :progress="getSetupProgress"
            :trial-line="statusLine"
          />

          <!-- Account setup: starts with the pre-completed email step -->
          <SetupSection
            v-if="accountSection"
            :id="`setup-section-${accountSection.id}`"
            class="section-anchor"
            :section="accountSection"
            :steps="stepsBySection[accountSection.id] || []"
            :progress="getSetupProgress.bySection[accountSection.id]"
            :phone="isPhone"
            :skipping-step-id="skippingStepId"
            @cta="handleStepCta"
            @skip="handleStepSkip"
          />
        </div>

        <!-- Sticky jump-to-section nav: outside the 5xl container so it can stretch to full width once it pins -->
        <div
          ref="navWrap"
          class="sticky top-0 z-30 mt-4"
        >
          <SetupSectionNav
            :title="content.SETUP_SECTION_NAV_TITLE"
            :compact-title="content.SETUP_SECTION_NAV_TITLE_COMPACT"
            :sections="navSections"
            :percent="getSetupProgress.percent"
            :stuck="isNavStuck"
            :phone="isPhone"
          />
        </div>

        <div
          class="max-w-5xl mx-auto w-full"
          :class="isPhone && !getSetupComplete ? 'pb-32' : 'pb-6'"
        >
          <!-- The three core areas -->
          <SetupSection
            v-for="section in coreSections"
            :id="`setup-section-${section.id}`"
            :key="section.id"
            class="section-anchor"
            :section="section"
            :steps="stepsBySection[section.id] || []"
            :progress="getSetupProgress.bySection[section.id]"
            :phone="isPhone"
            :skipping-step-id="skippingStepId"
            @cta="handleStepCta"
            @skip="handleStepSkip"
          />
        </div>
      </template>

      <!-- Modals -->
      <InviteTeamMemberModal
        v-if="showInviteModal"
        @close="showInviteModal = false"
        @showTeamSeatModal="openTeamSettings"
        @invitationSent="fetchSetupProgress({ force: true })"
      />
      <InstagramModal
        v-if="showInstagramModal"
        force-connect-step
        @close="closeInstagramModal"
        @connected="handleInstagramConnected"
      />
      <ConnectMCPModal
        v-if="showConnectMcpModal"
        @close="closeConnectMcpModal"
      />
      <!-- Intro modal that opens every guided flow -->
      <SetupTourIntroModal
        v-if="activeIntro"
        :intro="tourIntros[activeIntro.id]"
        :intro-id="activeIntro.id"
        :context="tourBrand ? { brandName: tourBrand.name } : {}"
        :cta-label="introCtaLabel"
        :loading="unlockingApi"
        @start="startIntro"
        @close="activeIntro = null"
      />
      <!-- "Track a Brand in Spyder" guided flow: curated picker, then the brand page tour -->
      <SetupBrandPickerModal
        v-if="showBrandPickerModal"
        @tracked="handleBrandTracked"
        @close="showBrandPickerModal = false"
      />
      <!-- "Create & Share a Board" guided flow: create modal, then seed ads -->
      <ManageBoardModal
        v-if="showManageBoardModal"
        :board="{}"
        :suggested-names="content.BOARD_NAME_SUGGESTIONS"
        @created="handleBoardCreated"
        @close="closeManageBoardModal"
      />
      <SetupBoardSeedModal
        v-if="seedBoard"
        :board-id="seedBoard.id"
        :board-name="seedBoard.name"
        :brand="tourBrand"
        @seeded="handleBoardSeeded"
        @close="closeSeedModal"
      />
      <!-- Showcase ad for the "Save Your First Ad" guided flow -->
      <AdvertisementDetailsDrawer
        v-if="showShowcaseDrawer"
        :advertisement="showcaseAd"
        discovery
        @close="closeShowcaseDrawer"
        @adSaved="handleShowcaseAdSaved"
      />
    </template>
  </MainLayout>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import posthog from 'posthog-js'
import ForeplayAPI from '@/api/foreplayServer'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import * as content from '@/utils/setup/setupContent'
import { SHOWCASE_AD, TOUR_INTROS, TOURS } from '@/utils/setup/tourContent'
import { getTrialDaysLeft } from '@/utils/trialUtils'

import MainLayout from '../../layouts/MainLayout.vue'
import SetupHeader from '../../components/setup/SetupHeader.vue'
import SetupSection from '../../components/setup/SetupSection.vue'
import InviteTeamMemberModal from '../../components/settings/team/InviteTeamMemberModal.vue'
import InstagramModal from '../../components/dashboard/Modals/InstagramModal.vue'
import ConnectMCPModal from '../../components/api/mcp/ConnectMCPModal.vue'
import SetupSectionNav from '../../components/setup/SetupSectionNav.vue'
import SetupPhoneHandoff from '../../components/setup/SetupPhoneHandoff.vue'
import AdvertisementDetailsDrawer from '../../components/advertisements/AdvertisementDetailsDrawer.vue'
import ManageBoardModal from '../../components/boards/ManageBoardModal.vue'
import SetupBoardSeedModal from '../../components/setup/SetupBoardSeedModal.vue'
import SetupBrandPickerModal from '../../components/setup/SetupBrandPickerModal.vue'
import SetupTourIntroModal from '../../components/tours/SetupTourIntroModal.vue'

// The brand picked in the Spyder guided flow, reused by the board flow so
// every walkthrough talks about the same brand
const TOUR_BRAND_STORAGE_KEY = 'fp-setup-tour-brand'

// Portrait phones by width; landscape phones by a coarse pointer and short height
const PHONE_MEDIA_QUERY = '(max-width: 639px), (pointer: coarse) and (max-height: 500px)'
// utm_source on the link copied from the phone hand-off
const DESKTOP_LINK_SOURCE = 'setup_phone_copy'

const matchesPhone = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_MEDIA_QUERY).matches

export default {
  name: 'SetupView',
  components: {
    MainLayout,
    SetupHeader,
    SetupSection,
    InviteTeamMemberModal,
    InstagramModal,
    ConnectMCPModal,
    SetupSectionNav,
    SetupPhoneHandoff,
    AdvertisementDetailsDrawer,
    ManageBoardModal,
    SetupBoardSeedModal,
    SetupBrandPickerModal,
    SetupTourIntroModal
  },
  data () {
    return {
      content,
      progressFetched: false,
      isNavStuck: false,
      // Phones get a read-only Launchpad; tours and modals only run on bigger screens
      isPhone: matchesPhone(),
      showInviteModal: false,
      showInstagramModal: false,
      showConnectMcpModal: false,
      showShowcaseDrawer: false,
      showcaseAd: null,
      // { id, cta } of the guided flow whose intro modal is open
      activeIntro: null,
      // MCP intro's "Unlock API" request is in flight
      unlockingApi: false,
      tourIntros: TOUR_INTROS,
      showBrandPickerModal: false,
      showManageBoardModal: false,
      // { id, name } of the board just created in the board flow, while the seed modal is up
      seedBoard: null,
      // Step whose skip / undo request is in flight
      skippingStepId: null
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUserFirstName', 'getUserFreeTrialDate', 'isFreeTier', 'getTeam', 'getHasApiAccess']),
    ...mapGetters('SetupModule', ['getSetupSteps', 'getSetupProgress', 'getSetupComplete', 'getSetupProgressLoaded']),
    ...mapGetters('TourModule', ['getActiveTour', 'getActiveTourStep']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions']),
    ...mapGetters('MiscModule', ['getSidebarCollapsed']),
    ...mapGetters('RouterModule', ['getLastRoute']),
    introCtaLabel () {
      // If user doesnt have api already, "Unlock API" else "Continue"
      const intro = this.activeIntro && TOUR_INTROS[this.activeIntro.id]
      if (!intro || !intro.unlockCtaLabel || this.getHasApiAccess) return null
      return intro.unlockCtaLabel
    },
    // The brand walkthroughs talk about: the one picked in the Spyder flow,
    // falling back to the workspace's first tracked brand
    tourBrand () {
      try {
        const stored = JSON.parse(localStorage.getItem(TOUR_BRAND_STORAGE_KEY) || 'null')
        if (stored && stored.brandId) return stored
      } catch (error) {
        // Unreadable storage — fall through to subscriptions
      }
      const subscription = this.getSpyderSubscriptions.find(entry => entry.id)
      return subscription ? { brandId: subscription.id, name: subscription.name, avatar: subscription.avatar || null } : null
    },
    // Spinner only until progress first loads; later visits refresh in the background
    showLoading () {
      return !this.getSetupProgressLoaded && !this.progressFetched
    },
    headerCopy () {
      const { kicker, title, subhead, progressLabel, progressLabelComplete } = this.content.SETUP_HEADER
      return {
        kicker,
        subhead,
        headline: title.replace('{name}', this.getUserFirstName ? `, ${this.getUserFirstName}` : ''),
        progressLabel: this.getSetupComplete ? progressLabelComplete : progressLabel
      }
    },
    // Account setup renders above the section nav; the core areas below it
    accountSection () {
      return this.content.SETUP_SECTIONS.find(section => section.id === 'account')
    },
    coreSections () {
      return this.content.SETUP_SECTIONS.filter(section => section.id !== 'account')
    },
    navSections () {
      return this.coreSections
        .filter(section => section.navLabel)
        .map(section => ({
          id: section.id,
          navLabel: section.navLabel,
          progress: this.getSetupProgress.bySection[section.id] || { done: 0, total: 0, complete: false }
        }))
    },
    statusLine () {
      if (this.getSetupComplete) return this.content.SETUP_HEADER.completeLine
      if (!this.isFreeTier) return null

      const daysLeft = getTrialDaysLeft(this.getUserFreeTrialDate)
      if (!daysLeft) return null
      return this.content.SETUP_HEADER.trialLine.replace('{days}', daysLeft)
    },
    stepsBySection () {
      const bySection = {}
      this.getSetupSteps.forEach(step => {
        if (!bySection[step.section]) bySection[step.section] = []
        bySection[step.section].push({ ...step, content: this.content.SETUP_STEP_CONTENT[step.id] })
      })
      return bySection
    }
  },
  watch: {
    // Flows that run in a modal on this page close it once their tour ends (finished or skipped)
    getActiveTour (tour, previous) {
      if (tour || previous?.id !== 'board-share') return
      this.showManageBoardModal = false
      this.seedBoard = null
    }
  },
  mounted () {
    if (window.matchMedia) {
      this.phoneQuery = window.matchMedia(PHONE_MEDIA_QUERY)
      this.updatePhone()
      // Safari < 14 only has the deprecated addListener
      if (this.phoneQuery.addEventListener) this.phoneQuery.addEventListener('change', this.updatePhone)
      else this.phoneQuery.addListener(this.updatePhone)
    }
    EventBus.$on('tour:step-enter', this.handleTourStepEnter)
    EventBus.$on('tour:open-manage-board', this.openManageBoardFromTour)
  },
  beforeDestroy () {
    if (this.phoneQuery) {
      if (this.phoneQuery.removeEventListener) this.phoneQuery.removeEventListener('change', this.updatePhone)
      else this.phoneQuery.removeListener(this.updatePhone)
    }
    EventBus.$off('tour:step-enter', this.handleTourStepEnter)
    EventBus.$off('tour:open-manage-board', this.openManageBoardFromTour)
  },
  async created () {
    // The expanded sidebar leaves a phone almost no room for the page. Only on
    // entry: the router-view is keyed by fullPath, so query changes (settings
    // drawer, etc.) re-create this view without overriding a manual expand.
    if (this.isPhone && !this.getSidebarCollapsed && this.getLastRoute !== 'SetupView') {
      this.SET_SIDEBAR_COLLAPSED(true)
      localStorage.setItem('sidebarCollapsed', true)
    }
    posthog.capture('Setup Page Viewed', { device: this.isPhone ? 'phone' : 'tablet_desktop' })
    const utmSource = this.$route.query.utm_source
    if (!this.isPhone && utmSource === DESKTOP_LINK_SOURCE) {
      posthog.capture('Setup Opened From Desktop Link', { utm_source: utmSource })
    }
    await this.fetchSetupProgress({ force: true })
    this.progressFetched = true
  },
  methods: {
    ...mapActions('SetupModule', ['fetchSetupProgress', 'setStepSkipped', 'stampStep', 'markStepCompleted']),
    ...mapActions('TourModule', ['startTour', 'endTour', 'advanceTour', 'updateTourContext']),
    ...mapActions('LensModule', ['enterDemoLens']),
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE', 'SET_SUBSCRIPTION_DRAWER_VIEW_MODE', 'SET_SIDEBAR_COLLAPSED']),
    updatePhone () {
      this.isPhone = this.phoneQuery.matches
    },
    // The nav is "stuck" once its wrapper pins to the top of MainLayout's scroll container
    handleLayoutScroll () {
      const wrap = this.$refs.navWrap
      if (!wrap || !this.$el) return
      const stuck = wrap.getBoundingClientRect().top - this.$el.getBoundingClientRect().top <= 1
      if (stuck !== this.isNavStuck) this.isNavStuck = stuck
    },
    // Phones get a read-only page: no step may open a tour or modal
    handleStepCta (step) {
      if (this.isPhone) return
      if (step.productLocked) {
        posthog.capture('Setup Upgrade Clicked', { step_id: step.id, product: step.product })
      } else {
        posthog.capture('Setup Step CTA Clicked', { step_id: step.id, section: step.section })
      }
      this.runStepCta(step)
    },
    async handleStepSkip (step) {
      if (this.isPhone || this.skippingStepId) return
      const skipped = !step.skipped

      this.skippingStepId = step.id
      try {
        await this.setStepSkipped({ stepId: step.id, skipped })
      } catch (error) {
        console.error('Error skipping setup step', step.id, error)
        this.$showAlert({ type: 'error', message: this.content.SETUP_SKIP.errorMessage })
      } finally {
        this.skippingStepId = null
      }
    },
    runStepCta (step) {
      if (step.productLocked) {
        this.SET_SUBSCRIPTION_DRAWER_VIEW_MODE({ state: true })
        return
      }

      // The row disables its CTA until the prerequisite is done
      if (step.locked) return

      const cta = step.content?.cta
      if (!cta) return

      // Guided flows open with their intro modal; Start runs the action
      const introId = cta.tour || cta.flow || cta.modal
      if (TOUR_INTROS[introId]) {
        this.activeIntro = { id: introId, cta }
        return
      }
      this.runCta(cta)
    },
    runCta (cta) {
      if (cta.route) {
        this.$router.push(cta.route).catch(() => {})
      } else if (cta.href) {
        window.open(cta.href, '_blank')
      } else if (cta.modal) {
        this.openModal(cta.modal)
      } else if (cta.tour) {
        this.startTour({ tourId: cta.tour })
      } else if (cta.flow) {
        this.startFlow(cta.flow)
      }
    },
    async startIntro () {
      const { id, cta } = this.activeIntro
      // Resized down to a phone while the intro was open
      if (this.isPhone) {
        this.activeIntro = null
        return
      }
      // The MCP intro unlocks the API first; a failed unlock keeps the intro open
      if (id === 'mcp' && !this.getHasApiAccess) {
        const unlocked = await this.unlockApi()
        if (!unlocked || this.activeIntro?.id !== 'mcp') return
      }
      this.activeIntro = null
      this.runCta(cta)
    },
    async unlockApi () {
      this.unlockingApi = true
      try {
        await ForeplayAPI.Subscription.createAPISubscription({ operationType: 'create' })
        this.$showAlert({ type: 'success', message: 'API access unlocked' })
        posthog.capture('Setup API Unlocked')
        return true
      } catch (error) {
        this.$showAlert({
          type: 'error',
          message: error.response?.data?.message || 'Unable to unlock API access'
        })
        return false
      } finally {
        this.unlockingApi = false
      }
    },
    openModal (modal) {
      switch (modal) {
        case 'invite':
          // Inviting needs a team; the team settings drawer handles creating one
          if (this.getTeam?.id) this.showInviteModal = true
          else this.openTeamSettings()
          break
        case 'instagram':
          this.showInstagramModal = true
          break
        case 'mcp':
          // The MCP intro has already unlocked the API when it was needed
          this.showConnectMcpModal = true
          break
      }
    },
    startFlow (flow) {
      switch (flow) {
        case 'save-ad':
          this.openShowcaseAd()
          break
        case 'board-share':
          this.openBoardFlow()
          break
        case 'spyder-brand':
          this.showBrandPickerModal = true
          break
        case 'ad-rank':
          this.openAdRankTour()
          break
        case 'lens-top-performing':
        case 'lens-comparison':
          // Starting the demo walkthrough completes the step; nothing in the
          this.stampStep(TOURS[flow].completesStep)
          // The router also enters the demo on /lens/demo; doing it first puts
          // the demo Lens in the sidebar the moment the tour leaves this page
          this.enterDemoLens()
          this.startTour({ tourId: flow })
          break
      }
    },
    tourBrandContext (brand) {
      return { brandId: brand.brandId, brandName: brand.name, brandAvatar: brand.avatar || null }
    },
    // ---- Spyder flow: picked brand becomes the tour brand, then its brand page tour ----
    handleBrandTracked (brand) {
      this.showBrandPickerModal = false
      try {
        localStorage.setItem(TOUR_BRAND_STORAGE_KEY, JSON.stringify({ brandId: brand.brandId, name: brand.name, avatar: brand.avatar || null }))
      } catch (error) {
        // localStorage unavailable — later flows fall back to the tracked brands list
      }
      this.startTour({ tourId: 'spyder-brand', context: this.tourBrandContext(brand) })
    },
    // ---- Save-ad flow: showcase ad drawer + walkthrough, then a completion card ----
    async openShowcaseAd () {
      try {
        if (!this.showcaseAd) {
          const response = await ForeplayAPI.Ads.getSharedAd(SHOWCASE_AD.id, SHOWCASE_AD.sharedBy)
          const ad = response?.data?.advertisements?.[0]
          if (!ad) throw new Error('Showcase ad unavailable')
          this.showcaseAd = ad
        }
        this.showShowcaseDrawer = true
        // The overlay waits for the drawer to finish opening before revealing
        this.startTour({ tourId: 'save-ad' })
      } catch (error) {
        console.error('Error loading showcase ad', error)
        // Fall back to saving an ad from Discovery
        this.$router.push({ name: 'DiscoveryView' }).catch(() => {})
      }
    },
    closeShowcaseDrawer () {
      this.showShowcaseDrawer = false
      if (this.getActiveTour?.id === 'save-ad') this.endTour()
      this.fetchSetupProgress({ force: true })
    },
    // The walkthrough ends on the Save click; once the save actually lands,
    // close the drawer and play the completion card that hands off to boards
    async handleShowcaseAdSaved () {
      posthog.capture('Setup Showcase Ad Saved')
      if (this.getActiveTour?.id === 'save-ad') this.endTour()
      this.showShowcaseDrawer = false
      await this.$nextTick()

      const ad = this.showcaseAd || {}
      this.startTour({
        tourId: 'save-ad-done',
        context: {
          adThumbnail: ad.thumbnail || ad.image || null,
          adBrandName: ad.name || null,
          adBrandAvatar: ad.avatar || null
        }
      })
    },
    // ---- Ad Rank flow: walkthrough on the tour brand's Spyder page ----
    openAdRankTour () {
      const brand = this.tourBrand
      if (!brand) {
        // The row unlocks once a brand is tracked; if none is left, pick one first
        this.$showAlert({ type: 'info', message: 'Track a brand first — pick one to get started.' })
        this.showBrandPickerModal = true
        return
      }
      this.stampStep(TOURS['ad-rank'].completesStep)
      this.startTour({ tourId: 'ad-rank', context: this.tourBrandContext(brand) })
    },
    // ---- Board flow: sidebar create tip → create modal → seed ads → share ----
    // The seed modal fills the board with the tour brand's ads, or Discovery
    // ads when there isn't one (e.g. no Spyder on the plan)
    openBoardFlow () {
      const brand = this.tourBrand
      const context = brand ? this.tourBrandContext(brand) : { brandName: 'Discovery' }
      // The create modal opens when the tour reaches its name step
      this.startTour({ tourId: 'board-share', context })
    },
    handleTourStepEnter ({ tourId, stepId } = {}) {
      if (tourId === 'board-share' && stepId === 'name') this.showManageBoardModal = true
    },
    // The sidebar's Create Board item during the tour opens this page's modal
    openManageBoardFromTour () {
      this.showManageBoardModal = true
      if (this.getActiveTour?.id === 'board-share' && this.getActiveTourStep?.id === 'sidebar-create') {
        this.advanceTour()
      }
    },
    closeManageBoardModal () {
      this.showManageBoardModal = false
      // Closing without creating abandons the flow (a created board hands off
      // to the seed modal before the close event fires)
      if (!this.seedBoard && this.getActiveTour?.id === 'board-share') this.endTour()
    },
    handleBoardCreated (boardId, boardName = '') {
      posthog.capture('Setup Board Created', { board_id: boardId })
      this.seedBoard = { id: boardId, name: boardName }
      this.updateTourContext({ boardName: boardName.replace(/^#/, '') })
      EventBus.$emit('setup:board-created')
    },
    closeSeedModal () {
      this.seedBoard = null
      if (this.getActiveTour?.id === 'board-share') this.endTour()
    },
    async handleBoardSeeded ({ adCount = 0, adThumbnails = [] } = {}) {
      const boardId = this.seedBoard?.id
      if (!boardId) return
      // Merged into the tour context for the share preview card
      EventBus.$emit('setup:board-seeded', { adCount, adThumbnails })
      await this.$router.push({ name: 'BoardsDetailsView', params: { id: boardId } }).catch(() => {})
      this.seedBoard = null
    },
    openTeamSettings () {
      this.showInviteModal = false
      this.SET_SETTINGS_DRAWER_VIEW_MODE('team')
    },
    closeInstagramModal () {
      this.showInstagramModal = false
      this.fetchSetupProgress({ force: true })
    },
    handleInstagramConnected () {
      posthog.capture('Setup Instagram Connected')
      this.markStepCompleted('instagram_connected')
    },
    closeConnectMcpModal () {
      this.showConnectMcpModal = false
      this.fetchSetupProgress({ force: true })
    }
  }
}
</script>

<style scoped>
/* Land scrolled-to sections below the sticky section nav */
.section-anchor {
  scroll-margin-top: 72px;
}
</style>
